/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { Dispatch, StateUpdater } from 'preact/hooks';
import type {
  ChartDndProps,
  ChartSeriesData,
  DragStartDetail,
  DropDetail,
  ChartItemData,
  SelectionDetail
} from '#utils/UNSAFE_visTypes/chart';
import {
  getChartDragContext,
  getChartDropContext,
  isChartDraggable,
  getDragImageElements
} from '#hooks/PRIVATE_useChartDnd/dndChartUtils';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { DragImageProps, useDnd } from '#hooks/PRIVATE_useVisDnd';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';

type Props<K, D> = {
  levelsArray: GroupLevelItem[][];
  series: ChartSeriesData<K, D>[];
  getDataItem: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined;
  rootRef: RefObject<HTMLDivElement>;
  xSpace: Dimension;
  ySpace: Dimension;
  plotAreaSpace: Dimension;
  selectedIds: Set<K>;
  setDragOver: Dispatch<StateUpdater<{ accepted: boolean | undefined; type: string }>>;
  setDatatipDisabled: Dispatch<StateUpdater<boolean>>;
  onSelectionChange?: SelectionDetail<K, D>;
} & ChartDndProps<D>;

export const useChartDnd = <K, D extends ChartItemData<K>>({
  onDragStart,
  onDrag,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  rootRef,
  series,
  selectedIds,
  levelsArray,
  getDataItem,
  groupDraggable,
  itemsDraggable,
  xSpace,
  ySpace,
  plotAreaSpace,
  setDragOver,
  setDatatipDisabled,
  onSelectionChange
}: Props<K, D>) => {
  const draggable = isChartDraggable(
    series,
    levelsArray,
    getDataItem,
    groupDraggable,
    itemsDraggable
  );

  const getDragContext = function (target: HTMLElement, event: DragEvent) {
    return getChartDragContext<K, D>(
      target,
      levelsArray,
      rootRef,
      event,
      selectedIds,
      series,
      getDataItem,
      groupDraggable,
      itemsDraggable
    );
  };
  const getDropContext = function (event: DragEvent) {
    const context = getChartDropContext(xSpace, ySpace, plotAreaSpace, event);
    return context;
  };
  const getChartDragImage = function (props: DragImageProps<DragStartDetail<D>>) {
    return getDragImageElements(rootRef, props.context, props.event);
  };

  const onPreDndStart = function (context: DragStartDetail<D>) {
    if (context?.type === 'items') {
      const items = context.items;
      const ids = items.map((item) => item.data.id);
      const data = items.map((item) => item.data);
      onSelectionChange?.({ ids: [...ids], data: [...data] });
    }
  };

  const showDropEffect = function (context: DropDetail) {
    setDragOver({
      accepted: true,
      type: context.type
    });
  };

  const showRejectedDropEffect = function (context: DropDetail) {
    setDragOver({
      accepted: false,
      type: context.type
    });
  };

  const clearDropEffect = function () {
    setDragOver({
      accepted: undefined,
      type: ''
    });
  };

  const { dragProps, dropProps, content } = useDnd<DragStartDetail<D>, DropDetail>({
    onDragStart,
    onDrag,
    onDragEnd,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    getDragContext,
    getDropContext,
    getDragImage: getChartDragImage,
    onPreDndStart,
    setDatatipDisabled,
    showRejectedDropEffect,
    showDropEffect,
    clearDropEffect
  });
  return {
    draggable: draggable,
    dndProps: draggable ? { ...dragProps, ...dropProps } : dropProps,
    dndContent: content
  };
};
